import { AxiosError } from 'axios';
import { UseMutationOptions } from 'react-query';

import useAPIMutation from '../api/useAPIMutation';
import { t } from '../i18n';
import { StageFormFormat } from '../pages/EditOpportunity/StagesForm/StagesForm';
import { PaginatedResponse, JobPostingDetail, StageCollection } from '../types';
import { EmailCommunicationTemplateCollectionInputSerializer } from '../types/api';

interface StagesMutationVariables {
  jobPostingId: JobPostingDetail['id'];
  stages: StageFormFormat[];
}

const useStagesMutation = (
  options?: Omit<
    UseMutationOptions<void, AxiosError<unknown, unknown>, StagesMutationVariables, unknown>,
    'mutationFn'
  >
) => {
  return useAPIMutation(async (variables: StagesMutationVariables, api) => {
    const saveTestsTemplate = async (stage: StageCollection, stageVariable: StageFormFormat) => {
      if (
        stage.email_communication_template &&
        stageVariable.email_communication_template !== stage.email_communication_template
      ) {
        const associatedTemplate = await api.get(
          `/email-communication-templates/${stage.email_communication_template}/`
        );

        await api.patch(`/stages/${stage.id}/`, {
          email_communication_template: null,
        });

        if (associatedTemplate.data?.individual) {
          await api.delete(`/email-communication-templates/${stage.email_communication_template}/`);
        }
      }

      if (stageVariable.email_communication_template) {
        return await api.patch(`/stages/${stage.id}/`, {
          email_communication_template: stageVariable.email_communication_template,
        });
      }

      if (!stageVariable.fromName || !stageVariable.subject || !stageVariable.message) {
        return;
      }

      const attachmentIds: number[] = [];

      for (const attachment of stageVariable.attachments || []) {
        if (attachment.originFileObj) {
          const attachmentFormData = new FormData();
          attachmentFormData.append('filename', attachment.name);
          attachmentFormData.append('file', attachment.originFileObj as File, attachment.name);
          attachmentFormData.append('mimetype', attachment.type as string);
          const attachmentResponse = await api.post('/attachments/', attachmentFormData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          attachmentIds.push(attachmentResponse.data.id);
        } else {
          attachmentIds.push(parseInt(attachment.uid, 10));
        }
      }

      const emailCommunicationTemplate = await api.post('/email-communication-templates/', {
        individual: true,
        subject: stageVariable.subject,
        message: stageVariable.message,
        type: 'TESTS',
        from_name: stageVariable.fromName,
        attachments: attachmentIds,
      });

      return await api.patch(`/stages/${stage.id}/`, {
        email_communication_template: emailCommunicationTemplate.data.id,
      });
    };

    const getEmailCommunicationTemplateId = async (
      stageVariable: StageFormFormat,
      emailCommunicationType: EmailCommunicationTemplateCollectionInputSerializer['type'],
      defaultSubject = ''
    ) => {
      if (stageVariable.id) {
        const stage = await api.get(`/stages/${stageVariable.id}/`);

        // Clears existing template if provided is different
        if (
          stage.data.email_communication_template &&
          stageVariable.email_communication_template !== stage.data.email_communication_template
        ) {
          const associatedTemplate = await api.get(
            `/email-communication-templates/${stage.data.email_communication_template}/`
          );

          await api.patch(`/stages/${stage.data.id}/`, {
            email_communication_template: null,
          });

          if (associatedTemplate.data?.individual) {
            await api.delete(
              `/email-communication-templates/${stage.data.email_communication_template}/`
            );
          }
        }
      }

      // Uses the email_communication_template of the interview_script_template if it's not personalized
      if (stageVariable.email_communication_template !== 0) {
        return null;
      }

      const attachmentIds: number[] = [];

      for (const attachment of stageVariable.attachments || []) {
        if (attachment.originFileObj) {
          const attachmentFormData = new FormData();
          attachmentFormData.append('filename', attachment.name);
          attachmentFormData.append('file', attachment.originFileObj as File, attachment.name);
          attachmentFormData.append('mimetype', attachment.type as string);
          const attachmentResponse = await api.post('/attachments/', attachmentFormData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          attachmentIds.push(attachmentResponse.data.id);
        } else {
          attachmentIds.push(parseInt(attachment.uid, 10));
        }
      }

      const config = await api.get('/configs/tenant-configurations/');

      const emailCommunicationTemplate = await api.post('/email-communication-templates/', {
        individual: true,
        subject: stageVariable.subject || defaultSubject,
        message: stageVariable.message,
        type: emailCommunicationType,
        from_name: stageVariable.fromName || config.data.companyFromName,
        attachments: attachmentIds,
      });

      return emailCommunicationTemplate.data.id;
    };

    let stages: StageCollection[] = [];
    let stagesPage: PaginatedResponse<StageCollection> = { next: undefined, count: 0, results: [] };

    do {
      stagesPage = (
        stagesPage.next
          ? await api.get(stagesPage.next)
          : await api.get(`/stages/`, {
              params: {
                job_posting_id: variables.jobPostingId,
                type: 'CUSTOM',
              },
            })
      ).data;

      stages = stages.concat(stagesPage.results);
    } while (stagesPage.next);

    const subscribedStages = await api.get(`/stages/`, {
      params: {
        job_posting_id: variables.jobPostingId,
        type: 'SUBSCRIBED',
      },
    });
    const subscribedStage = subscribedStages.data.results[0];

    const subscribedStageVariable = variables.stages.find(
      (stageVariable) => stageVariable.type === 'SUBSCRIBED'
    ) as StageFormFormat;

    await saveTestsTemplate(subscribedStage, subscribedStageVariable);

    await api.patch(`/stages/${subscribedStage.id}/`, {
      send_tests: subscribedStageVariable.testTypeList.length > 0,
      tests: subscribedStageVariable.testTypeList as StageCollection['tests'],
    });

    for (const stage of stages) {
      if (!variables.stages.find((formStage) => formStage.id === stage.id)) {
        await api.delete(`/stages/${stage.id}/`);
      }
    }

    for (let i = 0; i < variables.stages.length; i++) {
      const stage = variables.stages[i];
      if (stage.fixed) {
        continue;
      }

      if (stage.id) {
        await api.patch(`/stages/${stage.id}/`, {
          name: stage.name,
          sequence: i,
          sub_type: stage.sub_type,
          ...(stage.lock_interview_script
            ? {}
            : {
                interview_script_template: stage.interview_script_template || null,
                interview_script_content: !stage.interview_script_template
                  ? stage.interview_script_content
                  : '',
                ...(stage.sub_type === 'PRACTICAL_TECHNICAL_CASE' && stage.message
                  ? {
                      email_communication_template: await getEmailCommunicationTemplateId(
                        stage,
                        'PRACTICAL_TECHNICAL_CASE',
                        'Case prático ou técnico'
                      ),
                    }
                  : {}),
              }),
        });
      } else {
        await api.post('/stages/', {
          job_posting: variables.jobPostingId,
          name: stage.name,
          sequence: i,
          sub_type: stage.sub_type,
          interview_script_template: stage.interview_script_template || null,
          interview_script_content: !stage.interview_script_template
            ? stage.interview_script_content
            : '',
          ...(stage.sub_type === 'PRACTICAL_TECHNICAL_CASE' && stage.message
            ? {
                email_communication_template: await getEmailCommunicationTemplateId(
                  stage,
                  'PRACTICAL_TECHNICAL_CASE',
                  t('Case prático ou técnico') as string
                ),
              }
            : {}),
        });
      }
    }
  }, options);
};

export default useStagesMutation;
